import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronRight,
  Video,
  FileText,
  BookOpen,
  Layers,
  FolderOpen,
  Lock,
  CheckCircle2,
  Play
} from 'lucide-react'
import { toast } from 'sonner'
import { contentApi, ContentHierarchyNode } from '@/api/content'
import { learningSessionApi } from '@/api/learningSession'
import { cn } from '@/lib/utils'
import type { LessonSequenceMode, ModuleLearningConfig } from '@/types/configuration'

interface ContentSidebarProps {
  contentId: number
  currentLessonId?: number
  currentContentId?: number
  currentContentType?: string
  className?: string
  onLessonSelect?: (lessonId: number, parentModuleId?: number) => void
  onContentSelect?: (contentId: number, contentType: string) => void
  onModuleActivitySelect?: (moduleId: number, activityType: 'start' | 'end') => void
  // 레슨 순서 강제 관련 props
  lessonSequenceMode?: LessonSequenceMode
  completedLessonIds?: number[]
  isLoadingCompletedLessons?: boolean
  // 재개할 레슨 ID (첫 미완료 레슨)
  resumeLessonId?: number
  // MyCourses에서 모듈 클릭 시 초기 선택할 모듈 ID
  initialModuleId?: number
  moduleLearning?: ModuleLearningConfig | null
  // Configuration 조회용
  classProductId?: number
  // 외부에서 모듈 활동 선택 시 사이드바 포커스 동기화 (e.g. "123-end")
  activeModuleActivityKey?: string | null
}

// 콘텐츠 타입별 아이콘
const getTypeIcon = (type: string) => {
  switch (type) {
    case 'curriculum':
      return Layers
    case 'course':
      return BookOpen
    case 'module':
      return FolderOpen
    case 'lesson':
      return FileText
    default:
      return FileText
  }
}

// 재귀적으로 lesson을 찾아서 펼쳐야 할 노드들을 반환
const findPathToLesson = (
  nodes: ContentHierarchyNode[],
  targetId: number,
  path: number[] = []
): number[] | null => {
  for (const node of nodes) {
    if (node.id === targetId && node.type === 'lesson') {
      return path
    }
    if (node.children.length > 0) {
      const result = findPathToLesson(node.children, targetId, [...path, node.id])
      if (result) return result
    }
  }
  return null
}

// 첫 번째 lesson ID 찾기
const findFirstLessonId = (nodes: ContentHierarchyNode[]): number | null => {
  for (const node of nodes) {
    if (node.type === 'lesson') {
      return node.id
    }
    if (node.children.length > 0) {
      const result = findFirstLessonId(node.children)
      if (result) return result
    }
  }
  return null
}

// 모든 레슨을 flat하게 추출 (트리 순회 순서 = 시각적 순서 유지)
const getAllLessons = (nodes: ContentHierarchyNode[]): ContentHierarchyNode[] => {
  const lessons: ContentHierarchyNode[] = []

  const traverse = (nodeList: ContentHierarchyNode[]) => {
    // 각 레벨에서 sequenceOrder로 정렬 후 순회
    const sortedNodes = [...nodeList].sort((a, b) =>
      (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0)
    )

    for (const node of sortedNodes) {
      if (node.type === 'lesson') {
        lessons.push(node)
      }
      if (node.children.length > 0) {
        traverse(node.children)
      }
    }
  }

  traverse(nodes)

  // 트리 순회 순서 그대로 반환 (시각적 순서와 동일)
  return lessons
}

export function ContentSidebar({
  contentId,
  currentLessonId,
  currentContentId,
  currentContentType,
  className,
  onLessonSelect,
  onContentSelect,
  onModuleActivitySelect,
  lessonSequenceMode = 'flexible',
  completedLessonIds = [],
  isLoadingCompletedLessons = false,
  resumeLessonId,
  initialModuleId,
  moduleLearning,
  classProductId,
  activeModuleActivityKey
}: ContentSidebarProps) {
  const { t } = useTranslation('learner')
  const [openNodes, setOpenNodes] = useState<Record<number, boolean>>({})
  const [focusedActivityKey, setFocusedActivityKey] = useState<string | null>(null)

  // 외부에서 모듈 활동 선택 시 포커스 동기화
  useEffect(() => {
    if (activeModuleActivityKey !== undefined) {
      setFocusedActivityKey(activeModuleActivityKey)
    }
  }, [activeModuleActivityKey])

  const { data: hierarchy, isLoading } = useQuery({
    queryKey: ['contentHierarchy', contentId],
    queryFn: () => contentApi.getContentHierarchy(contentId),
    enabled: !!contentId,
    staleTime: 10 * 60 * 1000,
  })

  const moduleIds = useMemo(() => {
    if (!hierarchy) return []
    const ids: number[] = []
    const traverse = (nodes: ContentHierarchyNode[]) => {
      nodes.forEach(node => {
        if (node.type === 'module') ids.push(node.id)
        if (node.children.length > 0) traverse(node.children)
      })
    }
    traverse(hierarchy.children)
    return Array.from(new Set(ids))
  }, [hierarchy])

  const moduleActivityEnabled =
    moduleLearning?.startActivity?.enabled === true ||
    moduleLearning?.endActivity?.enabled === true

  const { data: moduleActivityStatuses = [], isLoading: isLoadingActivityStatuses, isFetched: isModuleActivityFetched } = useQuery({
    queryKey: ['moduleActivityStatuses', moduleIds.join(','), classProductId],
    queryFn: () => learningSessionApi.getModuleActivityStatuses(moduleIds, classProductId),
    enabled: moduleIds.length > 0 && moduleActivityEnabled,
    staleTime: 30 * 1000,
  })

  const moduleActivityStatusMap = useMemo(() => {
    return moduleActivityStatuses.reduce<Record<number, { startActivityEnabled: boolean; endActivityEnabled: boolean; startActivityCompleted: boolean; endActivityCompleted: boolean }>>(
      (acc, item) => {
        acc[item.moduleId] = {
          startActivityEnabled: item.startActivityEnabled,
          endActivityEnabled: item.endActivityEnabled,
          startActivityCompleted: item.startActivityCompleted,
          endActivityCompleted: item.endActivityCompleted,
        }
        return acc
      },
      {}
    )
  }, [moduleActivityStatuses])

  // 정렬된 레슨 목록 (순서 강제 및 계층 존재 확인에 사용)
  const sortedLessons = useMemo(() => {
    if (!hierarchy) return []
    return getAllLessons(hierarchy.children)
  }, [hierarchy])

  // 재귀적으로 타겟 노드까지의 경로를 찾는 함수
  const findPathToNode = (
    nodes: ContentHierarchyNode[],
    target: number,
    path: number[] = []
  ): number[] | null => {
    for (const node of nodes) {
      if (node.id === target) {
        return path
      }
      if (node.children.length > 0) {
        const result = findPathToNode(node.children, target, [...path, node.id])
        if (result) return result
      }
    }
    return null
  }

  // 초기 로드 시 경로 펼침: 마무리 퀴즈 대기 모듈 > 재개 레슨 > 첫 번째 레슨
  useEffect(() => {
    if (!hierarchy) return

    // 이미 열린 노드가 있으면 건드리지 않음 (사용자가 수동으로 조작한 경우)
    if (Object.keys(openNodes).length > 0) return

    // 마무리 퀴즈 체크를 위해 필수 데이터 로드 대기
    if (moduleLearning === undefined) return
    if (isLoadingCompletedLessons) return
    if (moduleActivityEnabled && isLoadingActivityStatuses) return
    if (moduleActivityEnabled && moduleIds.length > 0 && !isModuleActivityFetched) return

    // initialModuleId가 있으면 해당 모듈까지의 경로 펼침
    if (initialModuleId) {
      const path = findPathToNode(hierarchy.children, initialModuleId)
      if (path) {
        setOpenNodes(prev => {
          const newState = { ...prev }
          path.forEach(id => { newState[id] = true })
          newState[initialModuleId] = true
          return newState
        })
      }
      return
    }

    // 마무리 퀴즈 대기 모듈이 있으면 해당 모듈 경로만 펼침
    const pendingModuleId = findModuleWithPendingEndActivity()
    if (pendingModuleId) {
      const path = findPathToNode(hierarchy.children, pendingModuleId)
      if (path && path.length > 0) {
        setOpenNodes(prev => {
          const newState = { ...prev }
          path.forEach(id => { newState[id] = true })
          return newState
        })
      }
      return
    }

    // 재개할 레슨 또는 첫 번째 레슨 찾기 (resumeLessonId가 현재 계층에 존재하는지 확인)
    const resumeInHierarchy = resumeLessonId && sortedLessons.some(l => l.id === resumeLessonId)
    const targetLessonId = (resumeInHierarchy ? resumeLessonId : null) || findFirstLessonId(hierarchy.children)
    if (!targetLessonId) return

    const path = findPathToNode(hierarchy.children, targetLessonId)
    if (path && path.length > 0) {
      setOpenNodes(prev => {
        const newState = { ...prev }
        path.forEach(id => {
          newState[id] = true
        })
        return newState
      })
    }
  }, [hierarchy, resumeLessonId, initialModuleId, moduleLearning, isLoadingCompletedLessons, moduleActivityEnabled, isLoadingActivityStatuses, isModuleActivityFetched, moduleIds, moduleActivityStatusMap, completedLessonIds, sortedLessons])

  // 현재 선택된 lesson/content가 변경되면 해당 경로 펼침
  useEffect(() => {
    if (!hierarchy) return

    const targetId = currentLessonId || currentContentId
    if (!targetId) return

    const path = findPathToNode(hierarchy.children, targetId)
    if (path && path.length > 0) {
      setOpenNodes(prev => {
        const newState = { ...prev }
        path.forEach(id => {
          newState[id] = true
        })
        return newState
      })
    }
  }, [hierarchy, currentLessonId, currentContentId])

  // 마무리 퀴즈 미완료 모듈 찾기
  const findModuleWithPendingEndActivity = (): number | null => {
    if (!hierarchy || !moduleLearning?.endActivity?.enabled) return null
    // moduleActivityStatusMap이 비어있으면 아직 데이터 로딩 안됨
    if (Object.keys(moduleActivityStatusMap).length === 0) return null

    for (const moduleId of moduleIds) {
      const status = moduleActivityStatusMap[moduleId]
      // status가 없거나 endActivity가 비활성화면 건너뛰기
      if (!status || !status.endActivityEnabled) continue

      // 모듈의 모든 레슨이 완료되었는지 확인
      const moduleLessons: number[] = []
      const findLessonsInModule = (nodes: ContentHierarchyNode[], inModule: boolean) => {
        for (const n of nodes) {
          if (n.type === 'module' && n.id === moduleId) {
            findLessonsInModule(n.children, true)
          } else if (n.type === 'lesson' && inModule) {
            moduleLessons.push(n.id)
          } else if (n.children.length > 0) {
            findLessonsInModule(n.children, inModule)
          }
        }
      }
      findLessonsInModule(hierarchy.children, false)

      const allLessonsCompleted = moduleLessons.length > 0 &&
        moduleLessons.every(id => completedLessonIds.includes(id))

      if (allLessonsCompleted && !status.endActivityCompleted) {
        return moduleId
      }
    }
    return null
  }

  // 첫 로드 시 자동 선택: 마무리 퀴즈 대기 모듈 > resumeLessonId > 첫 번째 레슨
  useEffect(() => {
    // 필수 데이터 로딩 대기
    // 1. moduleLearning 설정이 로드될 때까지 대기 (마무리 퀴즈 체크를 위해 필수)
    if (moduleLearning === undefined) return
    // 2. 완료 레슨 목록 로딩 대기
    if (isLoadingCompletedLessons) return
    // 3. 모듈 활동이 활성화된 경우, 활동 상태 로딩 대기
    if (moduleActivityEnabled && isLoadingActivityStatuses) return
    // 4. 모듈 활동 상태 쿼리 완료 대기 (빈 결과와 미로딩을 구분)
    if (moduleActivityEnabled && moduleIds.length > 0 && !isModuleActivityFetched) return

    if (hierarchy && !currentContentId && onContentSelect) {
      // MyCourses에서 모듈 클릭으로 진입한 경우: 해당 모듈의 적절한 레슨 선택
      if (initialModuleId && onLessonSelect) {
        const findModuleNode = (nodes: ContentHierarchyNode[]): ContentHierarchyNode | null => {
          for (const node of nodes) {
            if (node.type === 'module' && node.id === initialModuleId) return node
            if (node.children.length > 0) {
              const result = findModuleNode(node.children)
              if (result) return result
            }
          }
          return null
        }

        const moduleNode = findModuleNode(hierarchy.children)
        if (moduleNode) {
          const moduleLessons = getAllLessons(moduleNode.children)
          if (moduleLessons.length > 0) {
            const allCompleted = moduleLessons.every(l => completedLessonIds.includes(l.id))
            let targetLesson: ContentHierarchyNode
            if (allCompleted) {
              // 마무리 퀴즈 미완료 시 → 모듈 선택 (ModuleOverview 표시)
              const moduleStatus = moduleActivityStatusMap[initialModuleId]
              if (moduleStatus?.endActivityEnabled && !moduleStatus?.endActivityCompleted) {
                setFocusedActivityKey(`${initialModuleId}-end`)
                onContentSelect?.(initialModuleId, 'module')
                return
              }
              // 마무리 퀴즈 완료 (또는 없음) → 첫 번째 레슨 포커싱
              targetLesson = moduleLessons[0]
            } else {
              // 진행 중인 모듈 → 첫 미완료 레슨 포커싱
              targetLesson = moduleLessons.find(l => !completedLessonIds.includes(l.id)) || moduleLessons[0]
            }
            onLessonSelect(targetLesson.id, initialModuleId)
            return
          }
        }
      }

      // 마무리 퀴즈 대기 중인 모듈이 있으면 해당 모듈 선택 + 경로 펼침
      const pendingModuleId = findModuleWithPendingEndActivity()
      if (pendingModuleId) {
        // 모듈까지의 경로 찾아서 모두 펼침
        const findPathToModule = (nodes: ContentHierarchyNode[], targetId: number, path: number[] = []): number[] | null => {
          for (const n of nodes) {
            if (n.id === targetId) return [...path, n.id]
            if (n.children.length > 0) {
              const result = findPathToModule(n.children, targetId, [...path, n.id])
              if (result) return result
            }
          }
          return null
        }
        const pathToModule = findPathToModule(hierarchy.children, pendingModuleId)
        if (pathToModule) {
          setOpenNodes(prev => {
            const newState = { ...prev }
            pathToModule.forEach(id => { newState[id] = true })
            return newState
          })
        }
        setFocusedActivityKey(`${pendingModuleId}-end`)
        onContentSelect(pendingModuleId, 'module')
        return
      }

      // resumeLessonId가 현재 계층 구조에 존재하면 해당 레슨 선택
      if (resumeLessonId && onLessonSelect) {
        const existsInHierarchy = sortedLessons.some(l => l.id === resumeLessonId)
        if (existsInHierarchy) {
          onLessonSelect(resumeLessonId, lessonToModuleMap[resumeLessonId])
          return
        }
      }

      // 첫 번째 레슨 선택
      const firstLessonId = findFirstLessonId(hierarchy.children)
      if (firstLessonId && onLessonSelect) {
        onLessonSelect(firstLessonId, lessonToModuleMap[firstLessonId])
      }
    }
  }, [hierarchy, currentContentId, onContentSelect, onLessonSelect, resumeLessonId, initialModuleId, moduleIds, moduleActivityStatusMap, completedLessonIds, moduleLearning, moduleActivityEnabled, isLoadingActivityStatuses, isModuleActivityFetched, isLoadingCompletedLessons, sortedLessons])

  const toggleNode = (nodeId: number) => {
    setOpenNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }))
  }

  // 레슨 ID → 소속 모듈 ID 매핑
  const lessonToModuleMap = useMemo(() => {
    const map: Record<number, number> = {}
    if (!hierarchy) return map
    const traverse = (nodes: ContentHierarchyNode[], parentModuleId: number | null) => {
      for (const node of nodes) {
        if (node.type === 'module') {
          traverse(node.children, node.id)
        } else if (node.type === 'lesson' && parentModuleId !== null) {
          map[node.id] = parentModuleId
        } else if (node.children.length > 0) {
          traverse(node.children, parentModuleId)
        }
      }
    }
    traverse(hierarchy.children, null)
    return map
  }, [hierarchy])

  // 특정 모듈이 잠겨있는지 확인 (이전 모듈의 마무리 퀴즈 미완료 시)
  const isModuleLocked = (moduleId: number): boolean => {
    // flexible 모드면 항상 접근 가능
    if (lessonSequenceMode !== 'sequential') return false

    // 마무리 퀴즈가 비활성화면 모듈 잠금 없음
    const hasEndActivity = moduleLearning?.endActivity?.enabled === true
    if (!hasEndActivity) return false

    // 모듈 순서 찾기
    const moduleIndex = moduleIds.indexOf(moduleId)
    if (moduleIndex <= 0) return false // 첫 번째 모듈은 항상 접근 가능

    // 이전 모듈의 마무리 퀴즈 완료 여부 확인
    const prevModuleId = moduleIds[moduleIndex - 1]
    const prevModuleStatus = moduleActivityStatusMap[prevModuleId]

    // 이전 모듈의 마무리 퀴즈가 활성화되어 있고 미완료면 잠금
    if (prevModuleStatus?.endActivityEnabled && !prevModuleStatus?.endActivityCompleted) {
      return true
    }

    return false
  }

  // 특정 레슨이 잠겨있는지 확인
  const isLessonLocked = (lessonId: number): boolean => {
    // 이미 완료된 레슨은 절대 잠금 상태가 아님
    if (completedLessonIds.includes(lessonId)) return false

    // flexible 모드면 항상 접근 가능
    if (lessonSequenceMode !== 'sequential') return false

    // 레슨이 속한 모듈이 잠겨있으면 레슨도 잠금
    const currentModuleId = lessonToModuleMap[lessonId]
    if (currentModuleId !== undefined && isModuleLocked(currentModuleId)) {
      return true
    }

    const lessonIndex = sortedLessons.findIndex(l => l.id === lessonId)
    // 첫 번째 레슨은 항상 접근 가능
    if (lessonIndex <= 0) return false

    // 이전 레슨이 완료되었는지 확인
    const prevLesson = sortedLessons[lessonIndex - 1]
    if (!completedLessonIds.includes(prevLesson.id)) return true

    // 모듈 경계 체크: 이전 레슨과 다른 모듈이면 이전 모듈의 마무리 퀴즈 완료 여부 확인
    const prevModuleId = lessonToModuleMap[prevLesson.id]

    if (currentModuleId !== prevModuleId && prevModuleId !== undefined) {
      // 이전 모듈에 마무리 퀴즈가 활성화되어 있는지 확인
      const hasEndActivity = moduleLearning?.endActivity?.enabled === true
      if (hasEndActivity) {
        const prevModuleStatus = moduleActivityStatusMap[prevModuleId]
        if (!prevModuleStatus?.endActivityCompleted) {
          return true // 이전 모듈 마무리 퀴즈 미완료 시 잠금
        }
      }
    }

    return false
  }

  const renderNode = (node: ContentHierarchyNode, depth: number = 0) => {
    const isLesson = node.type === 'lesson'
    const hasChildren = node.children.length > 0
    const isOpen = openNodes[node.id]
    const isCurrentLesson = isLesson && node.id === currentLessonId
    const isCurrentContent = !isLesson && node.id === currentContentId && node.type === currentContentType
    const Icon = getTypeIcon(node.type)
    const isLocked = isLesson && isLessonLocked(node.id)
    const isCompleted = isLesson && completedLessonIds.includes(node.id)

    if (isLesson) {
      // Lesson 노드 - 클릭 시 선택 (잠김 상태면 선택 차단)
      const handleLessonClick = () => {
        if (isLocked) {
          toast.warning(t('learning.sidebar.completePrevLesson'))
          return
        }
        setFocusedActivityKey(null)
        onLessonSelect?.(node.id, lessonToModuleMap[node.id])
        onContentSelect?.(node.id, node.type)
      }

      return (
        <button
          key={node.id}
          onClick={handleLessonClick}
          className={cn(
            "w-full group flex items-start gap-3 px-4 py-3 transition-all relative text-left",
            {
              "bg-indigo-50 hover:bg-indigo-50 text-indigo-900": isCurrentLesson,
              "text-gray-600 hover:bg-gray-50": !isCurrentLesson && !isLocked,
              "text-gray-400 cursor-not-allowed opacity-60": isLocked
            }
          )}
          style={{ paddingLeft: `${16 + depth * 16}px` }}
        >
          {isCurrentLesson && (
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600" />
          )}

          <div className={cn("mt-0.5 flex-shrink-0", {
            "text-indigo-600": isCurrentLesson,
            "text-green-600": isCompleted && !isCurrentLesson,
            "text-gray-400 group-hover:text-gray-600": !isCurrentLesson && !isLocked && !isCompleted,
            "text-gray-300": isLocked
          })}>
            {isLocked ? (
              <Lock className="w-4 h-4" />
            ) : isCompleted ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <Video className={cn("w-4 h-4", isCurrentLesson && "fill-indigo-100/50")} />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className={cn("text-sm transition-colors", {
              "font-bold text-indigo-700": isCurrentLesson,
              "font-medium group-hover:text-gray-900": !isCurrentLesson && !isLocked,
              "font-medium text-gray-400": isLocked
            })}>
              <ContentMetadataText text={node.bareTitle || node.title} />
            </div>
            {node.estimatedDurationMinutes && (
              <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-500 mt-1 inline-block">
                {t('learning.sidebar.minutes', { count: node.estimatedDurationMinutes })}
              </span>
            )}
          </div>
        </button>
      )
    }

    // 컨테이너 노드 (curriculum, course, module) - Chevron과 제목 클릭 분리
    const hasModuleStart = node.type === 'module' && moduleLearning?.startActivity?.enabled === true
    const hasModuleEnd = node.type === 'module' && moduleLearning?.endActivity?.enabled === true
    const moduleLocked = node.type === 'module' && isModuleLocked(node.id)

    const moduleStatus = node.type === 'module'
      ? moduleActivityStatusMap[node.id]
      : undefined

    // 모듈 내 레슨 목록 추출
    const getModuleLessons = (moduleNode: ContentHierarchyNode): ContentHierarchyNode[] => {
      const lessons: ContentHierarchyNode[] = []
      const traverse = (nodes: ContentHierarchyNode[]) => {
        for (const n of nodes) {
          if (n.type === 'lesson') lessons.push(n)
          if (n.children.length > 0) traverse(n.children)
        }
      }
      traverse(moduleNode.children)
      return lessons
    }

    const moduleLessons = node.type === 'module' ? getModuleLessons(node) : []
    const allModuleLessonsCompleted = moduleLessons.length > 0 &&
      moduleLessons.every(lesson => completedLessonIds.includes(lesson.id))

    const renderModuleActivityItem = (type: 'start' | 'end') => {
      const isCompleted = type === 'start'
        ? moduleStatus?.startActivityCompleted
        : moduleStatus?.endActivityCompleted

      // 마무리 퀴즈는 모든 레슨 완료 후에만 접근 가능
      const isLocked = type === 'end' && !allModuleLessonsCompleted

      const title = type === 'start' ? t('learning.sidebar.moduleStartQuiz') : t('learning.sidebar.moduleEndQuiz')
      const Icon = type === 'start' ? Play : CheckCircle2

      const handleClick = () => {
        if (isLocked) {
          toast.warning(t('learning.sidebar.completeAllLessons'))
          return
        }
        // 모듈 선택 → ModuleOverview에서 시작 버튼으로 퀴즈 진입
        setFocusedActivityKey(`${node.id}-${type}`)
        onContentSelect?.(node.id, 'module')
      }

      const isActivityFocused = focusedActivityKey === `${node.id}-${type}`

      return (
        <button
          key={`${node.id}-activity-${type}`}
          onClick={handleClick}
          className={cn(
            "w-full group flex items-start gap-3 px-4 py-2 transition-all relative text-left",
            isLocked
              ? "bg-gray-50 text-gray-400 cursor-not-allowed opacity-60"
              : isActivityFocused
                ? "bg-indigo-50 text-indigo-900"
                : "bg-amber-50/40 text-amber-800 hover:bg-amber-50"
          )}
          style={{ paddingLeft: `${16 + (depth + 1) * 16}px` }}
        >
          {isActivityFocused && (
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600" />
          )}
          <div className={cn("mt-0.5 flex-shrink-0", isLocked ? "text-gray-400" : isActivityFocused ? "text-indigo-600" : "text-amber-600")}>
            {isLocked ? (
              <Lock className="w-4 h-4" />
            ) : isCompleted ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <Icon className="w-4 h-4" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{title}</div>
            {isLocked && (
              <span className="text-[10px] text-gray-500">{t('learning.sidebar.availableAfterLessons')}</span>
            )}
            {!isLocked && isCompleted && (
              <span className="text-[10px] text-green-600">{t('learning.sidebar.completed')}</span>
            )}
          </div>
        </button>
      )
    }

    const moduleChildren = node.children
    const firstLessonIndex = moduleChildren.findIndex(c => c.type === 'lesson')
    const lastLessonIndex = moduleChildren.length - 1 - [...moduleChildren].reverse().findIndex(c => c.type === 'lesson')
    const hasLessonChildren = firstLessonIndex >= 0 && lastLessonIndex >= 0

    return (
      <div key={node.id} className="mb-0.5">
        <div className={cn(
          "flex items-center relative",
          { 
            "bg-gray-50": isOpen && !isCurrentContent,
            "bg-indigo-50": isCurrentContent && !focusedActivityKey
          }
        )}>
          {isCurrentContent && !focusedActivityKey && (
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600" />
          )}
          
          {/* Chevron 버튼 - 펼침/접힘만 (잠긴 모듈은 펼침 불가) */}
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (moduleLocked) return
                toggleNode(node.id)
              }}
              className={cn(
                "flex-shrink-0 p-3 rounded transition-colors",
                moduleLocked
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-400 hover:text-gray-600 hover:bg-gray-200"
              )}
              style={{ marginLeft: `${depth * 16}px` }}
              aria-label={isOpen ? t('learning.sidebar.collapse') : t('learning.sidebar.expand')}
            >
              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : (
            <div className="w-10 flex-shrink-0" style={{ marginLeft: `${depth * 16}px` }} />
          )}
          
          {/* 제목 클릭 시 펼치기/접기만 동작 (모듈 선택 X) */}
          <button
            onClick={() => {
              if (moduleLocked) {
                toast.warning(t('learning.sidebar.completePrevModuleQuiz'))
                return
              }
              setFocusedActivityKey(null)
              if (hasChildren) {
                toggleNode(node.id)
              }
            }}
            className={cn(
              "flex-1 flex items-center gap-3 py-3 pr-4 text-left transition-colors",
              moduleLocked ? "cursor-not-allowed opacity-60" : "hover:bg-gray-100"
            )}
          >
            <div className={cn("flex-shrink-0", {
              "text-indigo-600": node.type === 'course',
              "text-amber-600": node.type === 'module' && !moduleLocked,
              "text-gray-400": moduleLocked,
              "text-purple-600": node.type === 'curriculum'
            })}>
              {moduleLocked ? <Lock className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className={cn("text-sm font-semibold truncate", {
                "text-indigo-700": isCurrentContent && !focusedActivityKey,
                "text-gray-800": !isCurrentContent && !moduleLocked,
                "text-gray-400": moduleLocked
              })}>
                <ContentMetadataText text={node.bareTitle || node.title} />
              {import.meta.env.DEV && node.type === 'module' && (
                <span className="ml-1 text-[9px] text-gray-400 font-mono">#{node.id}</span>
              )}
              </div>
              {moduleLocked && (
                <span className="text-[10px] text-gray-500">{t('learning.sidebar.prevModuleQuizRequired')}</span>
              )}
            </div>
          </button>
        </div>

        {isOpen && hasChildren && (
          <div className="bg-white border-y border-gray-100">
            {moduleChildren.map((child, index) => {
              return (
                <div key={child.id}>
                  {hasModuleStart && hasLessonChildren && index === firstLessonIndex && renderModuleActivityItem('start')}
                  {renderNode(child, depth + 1)}
                  {hasModuleEnd && hasLessonChildren && index === lastLessonIndex && renderModuleActivityItem('end')}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={cn("w-80 bg-gray-50 border-r border-gray-200 h-full p-4", className)}>
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-6 animate-pulse" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="space-y-2">
              <div className="h-10 bg-gray-200 rounded animate-pulse" />
              <div className="h-6 bg-gray-200 rounded w-1/2 ml-4 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!hierarchy) {
    return (
      <div className={cn("w-80 bg-gray-50 border-r border-gray-200 h-full p-4", className)}>
        <p className="text-gray-500 text-sm">{t('learning.sidebar.loadFailed')}</p>
      </div>
    )
  }

  return (
    <div className={cn("w-80 bg-gray-50 border-r border-gray-200 h-full flex flex-col", className)}>
      {/* Content Tree - 최상위 노드 제외, children만 표시 */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="py-2">
          {hierarchy.children.map(child => renderNode(child, 0))}
        </div>
      </div>
    </div>
  )
}









